import { useState, useMemo } from 'react'
import { ChevronDown, ChevronRight, Camera, Sparkles, AlertTriangle, SkipForward, UserPlus, FolderPlus } from 'lucide-react'
import { useDropActivityQuery, type ActivityLogEntry, type ActivityEventType } from '@/data/activity'

type Props = {
  dropId: string
}

type SessionGroup = {
  sessionId: string
  actorName: string | null
  entries: ActivityLogEntry[]
  latestAt: string
  counts: Partial<Record<ActivityEventType, number>>
}

const EVENT_ICONS: Record<ActivityEventType, { icon: typeof Camera; color: string }> = {
  photo_uploaded: { icon: Camera, color: 'text-blue-400' },
  photo_optimized: { icon: Sparkles, color: 'text-emerald-400' },
  photo_processing_failed: { icon: AlertTriangle, color: 'text-red-400' },
  photo_processing_skipped: { icon: SkipForward, color: 'text-zinc-400' },
  contributor_joined: { icon: UserPlus, color: 'text-violet-400' },
  drop_created: { icon: FolderPlus, color: 'text-zinc-400' },
}

function formatRelativeTime(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime()
  const mins = Math.floor(diff / 60000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs}h ago`
  return `${Math.floor(hrs / 24)}d ago`
}

function buildSessionSummary(group: SessionGroup): string {
  const parts: string[] = []
  const uploaded = group.counts.photo_uploaded || 0
  const optimized = group.counts.photo_optimized || 0
  const failed = group.counts.photo_processing_failed || 0
  const skipped = group.counts.photo_processing_skipped || 0

  if (uploaded) parts.push(`${uploaded} photo${uploaded > 1 ? 's' : ''} uploaded`)
  if (optimized) parts.push(`${optimized} optimized`)
  if (failed) parts.push(`${failed} failed`)
  if (skipped) parts.push(`${skipped} skipped`)

  return parts.join(' \u00b7 ')
}

function eventLabel(entry: ActivityLogEntry): string {
  const meta = entry.metadata
  switch (entry.eventType) {
    case 'photo_uploaded':
      return `Uploaded ${(meta.filename as string) || 'photo'}`
    case 'photo_optimized': {
      const orig = meta.originalSize as number
      const proc = meta.processedSize as number
      const pct = orig ? ((1 - proc / orig) * 100).toFixed(0) : '?'
      return `Optimized ${(meta.filename as string) || 'photo'} (${pct}% smaller)`
    }
    case 'photo_processing_failed':
      return `Failed: ${(meta.filename as string) || 'photo'} — ${(meta.reason as string) || 'unknown error'}`
    case 'photo_processing_skipped':
      return `Skipped: ${(meta.filename as string) || 'photo'}`
    case 'contributor_joined':
      return `${entry.actorName || 'Someone'} joined via invite link`
    case 'drop_created':
      return 'Drop created'
    default:
      return entry.eventType
  }
}

export function SessionActivityFeed({ dropId }: Props) {
  const { activities, isLoading, error } = useDropActivityQuery(dropId)
  const [expandedSessions, setExpandedSessions] = useState<Set<string>>(new Set())

  const { groups, ungrouped } = useMemo(() => {
    const sessionMap = new Map<string, ActivityLogEntry[]>()
    const ungrouped: ActivityLogEntry[] = []

    for (const entry of activities) {
      const sid = entry.sessionId
      if (sid) {
        if (!sessionMap.has(sid)) sessionMap.set(sid, [])
        sessionMap.get(sid)!.push(entry)
      } else {
        ungrouped.push(entry)
      }
    }

    const groups: SessionGroup[] = []
    for (const [sessionId, entries] of sessionMap) {
      const counts: Partial<Record<ActivityEventType, number>> = {}
      for (const e of entries) {
        counts[e.eventType] = (counts[e.eventType] || 0) + 1
      }
      groups.push({
        sessionId,
        actorName: entries.find(e => e.actorName)?.actorName ?? null,
        entries,
        latestAt: entries[0].createdAt,
        counts,
      })
    }

    return { groups, ungrouped }
  }, [activities])

  function toggleSession(sid: string) {
    setExpandedSessions(prev => {
      const next = new Set(prev)
      if (next.has(sid)) next.delete(sid)
      else next.add(sid)
      return next
    })
  }

  if (isLoading) {
    return <p className="text-sm text-muted-foreground py-4">Loading activity...</p>
  }

  if (error) {
    return <p className="text-sm text-red-400 py-4">Failed to load activity.</p>
  }

  if (activities.length === 0) {
    return (
      <p className="text-sm text-muted-foreground py-4">
        No activity yet. Events will appear here as contributors upload photos.
      </p>
    )
  }

  // Merge groups and ungrouped, sorted by latest timestamp
  const items: Array<{ type: 'session'; data: SessionGroup; sortKey: string } | { type: 'single'; data: ActivityLogEntry; sortKey: string }> = [
    ...groups.map(g => ({ type: 'session' as const, data: g, sortKey: g.latestAt })),
    ...ungrouped.map(e => ({ type: 'single' as const, data: e, sortKey: e.createdAt })),
  ].sort((a, b) => new Date(b.sortKey).getTime() - new Date(a.sortKey).getTime())

  return (
    <div className="space-y-2">
      {items.map(item => {
        if (item.type === 'single') {
          const entry = item.data as ActivityLogEntry
          const cfg = EVENT_ICONS[entry.eventType] || EVENT_ICONS.drop_created
          const Icon = cfg.icon
          return (
            <div key={entry.id} className="flex items-start gap-3 py-2">
              <Icon className={`h-4 w-4 mt-0.5 shrink-0 ${cfg.color}`} />
              <span className="text-sm flex-1">{eventLabel(entry)}</span>
              <span className="text-xs text-muted-foreground shrink-0">{formatRelativeTime(entry.createdAt)}</span>
            </div>
          )
        }

        const group = item.data as SessionGroup
        const isExpanded = expandedSessions.has(group.sessionId)
        const actorLabel = group.actorName || 'Contributor'

        return (
          <div key={group.sessionId} className="border rounded-lg">
            <button
              onClick={() => toggleSession(group.sessionId)}
              className="w-full flex items-center gap-3 p-3 hover:bg-accent/50 rounded-lg text-left"
            >
              {isExpanded
                ? <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
                : <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
              }
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{actorLabel}</p>
                <p className="text-xs text-muted-foreground">{buildSessionSummary(group)}</p>
              </div>
              <span className="text-xs text-muted-foreground shrink-0">{formatRelativeTime(group.latestAt)}</span>
            </button>
            {isExpanded && (
              <div className="px-3 pb-3 pl-10 space-y-1">
                {group.entries.map(entry => {
                  const cfg = EVENT_ICONS[entry.eventType] || EVENT_ICONS.drop_created
                  const Icon = cfg.icon
                  return (
                    <div key={entry.id} className="flex items-start gap-3 py-1.5">
                      <Icon className={`h-3.5 w-3.5 mt-0.5 shrink-0 ${cfg.color}`} />
                      <span className="text-xs flex-1">{eventLabel(entry)}</span>
                      <span className="text-[10px] text-muted-foreground shrink-0">{formatRelativeTime(entry.createdAt)}</span>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}
